// features/workstations/dashboard-sections/weekly-review.js
// ═══════════════════════════════════════════════════════════
// WEEKLY REVIEW (Batch FT-5) — the Sunday reflection surface.
//
// Shows the latest weekly review (auto-generated each Sunday
// 7am ET, or manually via the "Generate" button). Sections
// match the generator output:
//   • Headline + week stats
//   • Week summary (Sonnet narrative)
//   • Patterns active this week
//   • Behavior observations
//   • Emerging patterns
//   • The One Question (gold callout)
//   • Outcomes table (collapsible)
// ═══════════════════════════════════════════════════════════

import { useEffect, useState, useCallback } from 'react';
import { ShiftRead } from '../shared';

export default function WeeklyReviewSection({ workstation, userId }) {
  const [review, setReview] = useState(null);
  const [history, setHistory] = useState([]);   // TIGHT-4: prior weeks for cross-week callouts
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [showOutcomes, setShowOutcomes] = useState(false);

  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ workstation_id: workstation.id, user_id: userId, with_history: '1' });
      const res = await fetch(`/api/workstations/weekly-review?${qs}`, { cache: 'no-store' });
      const data = res.ok ? await res.json() : { review: null, history: [] };
      setReview(data.review);
      setHistory(Array.isArray(data.history) ? data.history : []);
    } catch (e) {
      console.warn('[weekly-review] load failed:', e?.message);
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = useCallback(async () => {
    if (!workstation?.id || !userId || generating) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await fetch('/api/workstations/weekly-review?action=generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, workstation_id: workstation.id }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || `HTTP ${res.status}`);
        return;
      }
      if (data.skipped) {
        setError(data.reason || 'Skipped — no trades this week.');
        return;
      }
      setReview(data.review);
    } catch (e) {
      setError(e?.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  }, [workstation?.id, userId, generating]);

  return (
    <section className="ws-section ws-section-weekly-review">
      <div className="ws-section-header">
        <div className="ws-section-label">Weekly Review</div>
        <div className="ws-section-tag">
          {review?.week_start && (
            <span>{review.week_start} → {review.week_end}</span>
          )}
        </div>
      </div>

      <div className="ws-section-body">
        {loading && <div className="ws-section-empty-text">Loading…</div>}

        {!loading && !review && (
          <div className="ws-weekly-empty">
            <div className="ws-weekly-empty-title">No weekly review yet</div>
            <div className="ws-weekly-empty-body">
              Sunday at 7 AM ET, Shift will generate a review of last week&rsquo;s
              trades — what worked, what didn&rsquo;t, which signals fired, your
              behavior patterns, and one reflective question for the week ahead.
              <br /><br />
              Or generate it now from the data you have so far.
            </div>
            <button
              type="button"
              className="ws-weekly-generate-btn"
              onClick={handleGenerate}
              disabled={generating}
            >
              {generating ? 'Generating…' : '+ Generate weekly review'}
            </button>
            {error && <div className="ws-weekly-error">{error}</div>}
          </div>
        )}

        {!loading && review && (
          <WeeklyReviewBody
            review={review}
            history={history}
            onRegenerate={handleGenerate}
            generating={generating}
            error={error}
            showOutcomes={showOutcomes}
            onToggleOutcomes={() => setShowOutcomes((s) => !s)}
          />
        )}
      </div>
    </section>
  );
}

// ─── Body ──────────────────────────────────────────────────
// TIGHT-4: story-first layout. Narrative + reflective question
// lead. Stats and outcomes are supporting evidence, not the
// headline. "Story over numbers" per the TIGHT-4 spec.
function WeeklyReviewBody({ review, history, onRegenerate, generating, error, showOutcomes, onToggleOutcomes }) {
  const summary  = review.week_summary || {};
  const patterns = Array.isArray(review.patterns_active) ? review.patterns_active : [];
  const behavior = review.behavior_observations || {};
  const emerging = Array.isArray(review.emerging_patterns) ? review.emerging_patterns : [];
  const oneQ     = review.one_question || {};
  const outcomes = Array.isArray(review.outcomes_table) ? review.outcomes_table : [];

  const winRate = review.trades_count > 0
    ? Math.round((review.wins_count / Math.max(review.wins_count + review.losses_count, 1)) * 100)
    : null;
  const totalR = Number(review.total_r);

  // TIGHT-4: Cross-week callouts. For each pattern this week,
  // count how many of the PRIOR weeks it also appeared in.
  // A "3rd consecutive week" badge surfaces from sustained
  // patterns — that's the story the numbers can't tell.
  const patternAppearances = computeConsecutiveAppearances(patterns, history || []);

  return (
    <div className="ws-weekly-body ws-weekly-body-story">
      {/* Title + regenerate */}
      <div className="ws-weekly-headline">
        <div className="ws-weekly-title">{review.title}</div>
        {review.summary && <div className="ws-weekly-summary">{review.summary}</div>}
        <button
          type="button"
          className="ws-weekly-regen-btn"
          onClick={onRegenerate}
          disabled={generating}
        >{generating ? 'Regenerating…' : '↻ Regenerate'}</button>
      </div>

      {error && <div className="ws-weekly-error">{error}</div>}

      {/* STORY-FIRST: The One Question LEADS — this is the
          reflective moment, not the closing note. Gold callout,
          largest visual weight. */}
      {oneQ.question && (
        <div className="ws-weekly-question ws-top-accent-gold ws-weekly-question-lead">
          <div className="ws-weekly-eyebrow ws-tone-gold">The One Question · For Next Week</div>
          <div className="ws-weekly-question-text">{oneQ.question}</div>
          {oneQ.why_it_matters && (
            <div className="ws-weekly-question-why">{oneQ.why_it_matters}</div>
          )}
        </div>
      )}

      {/* Week summary (narrative) — larger body text than before */}
      {summary.headline && (
        <div className="ws-weekly-block ws-weekly-summary-block">
          <div className="ws-weekly-eyebrow ws-tone-gold">Week Summary</div>
          <div className="ws-weekly-headline-text">{summary.headline}</div>
          {summary.body && <div className="ws-weekly-body-text ws-weekly-body-text-prominent">{summary.body}</div>}
        </div>
      )}

      {/* Patterns active — with cross-week callouts */}
      {patterns.length > 0 && (
        <div className="ws-weekly-block ws-top-accent-sage">
          <div className="ws-weekly-eyebrow ws-tone-sage">Patterns Active This Week</div>
          <ul className="ws-weekly-list">
            {patterns.map((p, i) => {
              const streak = patternAppearances[p.condition_name] || 1;
              return (
                <li key={i} className="ws-weekly-item">
                  <div className="ws-weekly-item-row">
                    <span className="ws-weekly-item-name">{p.condition_name}</span>
                    <span className="ws-pill ws-pill-sage">fired {p.fired_n}×</span>
                    {streak >= 2 && (
                      <span className={`ws-pill ws-pill-${streak >= 3 ? 'gold' : 'amber'}`}>
                        {streak === 2 ? '2nd week' : `${streak}${ordinalSuffix(streak)} consecutive week`}
                      </span>
                    )}
                  </div>
                  {p.outcomes_summary && <div className="ws-weekly-item-body">{p.outcomes_summary}</div>}
                  {streak >= 3 && (
                    <div className="ws-weekly-item-streak">
                      This has fired for {streak} weeks running. Worth asking: is
                      it a genuine tailwind/headwind, or a regime you haven&rsquo;t
                      adapted to yet?
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Behavior */}
      {(behavior.entry_type_breakdown || behavior.rule_compliance) && (
        <div className="ws-weekly-block ws-top-accent-purple">
          <div className="ws-weekly-eyebrow ws-tone-purple">Behavior Observations</div>
          {behavior.entry_type_breakdown && (
            <div className="ws-weekly-behavior-row">
              <span className="ws-weekly-behavior-label">Entry types:</span>
              <span className="ws-weekly-behavior-value"> {behavior.entry_type_breakdown}</span>
            </div>
          )}
          {behavior.rule_compliance && (
            <div className="ws-weekly-behavior-row">
              <span className="ws-weekly-behavior-label">Rule compliance:</span>
              <span className="ws-weekly-behavior-value"> {behavior.rule_compliance}</span>
            </div>
          )}
          {behavior.notes && (
            <div className="ws-weekly-body-text">{behavior.notes}</div>
          )}
        </div>
      )}

      {/* Emerging patterns */}
      {emerging.length > 0 && (
        <div className="ws-weekly-block ws-top-accent-amber">
          <div className="ws-weekly-eyebrow ws-tone-amber">Emerging Patterns</div>
          <ul className="ws-weekly-list">
            {emerging.map((e, i) => (
              <li key={i} className="ws-weekly-item">
                <div className="ws-weekly-item-row">
                  <span className="ws-weekly-item-name">{e.condition_name}</span>
                  <span className={`ws-pill ws-pill-${e.status === 'flipping' ? 'terra' : 'amber'}`}>
                    {e.status} · {e.sessions_matching} matches
                  </span>
                </div>
                {e.notes && <div className="ws-weekly-item-body">{e.notes}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Stats strip moved to BOTTOM — story leads, numbers support */}
      <div className="ws-weekly-stats ws-weekly-stats-supporting">
        <div className="ws-weekly-stats-eyebrow">The numbers behind it</div>
        <div className="ws-weekly-stats-row">
          <Stat label="Trades"     value={review.trades_count} />
          <Stat label="Wins"       value={review.wins_count}  tone="sage" />
          <Stat label="Losses"     value={review.losses_count} tone="terra" />
          <Stat label="Win rate"   value={winRate != null ? `${winRate}%` : '—'} tone={winRate != null && winRate >= 60 ? 'sage' : winRate != null && winRate < 40 ? 'terra' : 'amber'} />
          <Stat label="Net R"      value={Number.isFinite(totalR) ? `${totalR > 0 ? '+' : ''}${totalR.toFixed(2)}R` : '—'} tone={totalR > 0 ? 'sage' : totalR < 0 ? 'terra' : 'dim'} />
        </div>
      </div>

      {/* Outcomes table (collapsible) */}
      {outcomes.length > 0 && (
        <div className="ws-weekly-block">
          <div
            className="ws-weekly-outcomes-toggle"
            onClick={onToggleOutcomes}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onToggleOutcomes(); }}
          >
            <span className="ws-weekly-eyebrow">{showOutcomes ? '▾' : '▸'} Outcomes table · {outcomes.length} trades</span>
          </div>
          {showOutcomes && (
            <div className="ws-weekly-outcomes-table">
              {outcomes.map((t) => (
                <div key={t.id} className="ws-weekly-outcome-row">
                  <span className="ws-weekly-outcome-ticker">{t.ticker} {t.direction}</span>
                  {t.setup_type && <span className="ws-weekly-outcome-setup">{t.setup_type}</span>}
                  <span className={`ws-weekly-outcome-pnl ws-tone-${(t.realized_r ?? t.realized_pnl ?? 0) > 0 ? 'sage' : (t.realized_r ?? t.realized_pnl ?? 0) < 0 ? 'terra' : 'dim'}`}>
                    {t.realized_r != null ? `${t.realized_r > 0 ? '+' : ''}${t.realized_r.toFixed(2)}R` : `$${t.realized_pnl ?? 0}`}
                  </span>
                  {t.net_bias && <span className="ws-weekly-outcome-bias">{t.net_bias} day</span>}
                  {t.entry_type && <span className="ws-weekly-outcome-entry">{t.entry_type}</span>}
                  {t.rules_followed && t.rules_followed !== 'yes' && (
                    <span className="ws-pill ws-pill-terra">rules {t.rules_followed}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ShiftRead variant="purple">
        Five minutes once a week. Your data, structured. The reflective question
        is the most valuable part — most insights come from sitting with that
        question, not from reading the brief.
      </ShiftRead>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className={`ws-weekly-stat${tone ? ` ws-weekly-stat-${tone}` : ''}`}>
      <div className="ws-weekly-stat-value">{value}</div>
      <div className="ws-weekly-stat-label">{label}</div>
    </div>
  );
}

// TIGHT-4: count how many consecutive weeks each pattern has
// fired. Returns { pattern_name: streak_length } where streak
// includes the current week. History is expected newest-first.
function computeConsecutiveAppearances(currentPatterns, history) {
  if (!Array.isArray(currentPatterns) || currentPatterns.length === 0) return {};
  const result = {};
  for (const p of currentPatterns) {
    const name = p?.condition_name;
    if (!name) continue;
    let streak = 1;  // this week counts
    for (const priorWeek of history) {
      const priorPatterns = Array.isArray(priorWeek?.patterns_active)
        ? priorWeek.patterns_active
        : [];
      const found = priorPatterns.some((pp) => pp?.condition_name === name);
      if (!found) break;
      streak++;
    }
    result[name] = streak;
  }
  return result;
}

function ordinalSuffix(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}
